import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Controller } from 'react-hook-form';
import { Card, MenuItem, Select, Typography } from 'seagull';
import { amendmentsTextsPath, getAllowManualPaymentsChangeBullet, getAmendmentsApprovalMethodChangeBullet, getCoverCCFeesChangeBullet, getInvoicesApprovalMethodChangeBullet, getNetTermsChangeBullet, netTermsSelectOptions } from '~/business-logic/amendmentsLogic';
import { getAutoApprovalRange } from '~/business-logic/autoApprovalLogic';
import { useLocaleKeys } from '~/i18n';
import { approvalMethodNoticePeriodModel, relationshipModel } from '~/models';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import { AllowManualPaymentsCheckbox, ApprovalMethodNoticePeriodField, CoverCreditCardFeesSelector, validateApprovalMethodNoticePeriod } from '../agreement-controllers';
import AmendmentChanges from '../AmendmentChanges/AmendmentChanges';
import styles from './EditAgreementModal.module.scss';
import { useEditAgreementForm } from './utils/EditAgreementModal.utils';
export const Section = (props)=>/*#__PURE__*/ _jsx(Card, {
        bi: {
            name: 'edit_agreement_section_card',
            data: {
                section_title: props.title
            }
        },
        autoHeight: true,
        padding: "m",
        title: props.title,
        children: /*#__PURE__*/ _jsx("div", {
            className: styles.sectionContent,
            children: props.children
        })
    });
export const SettingsField = (props)=>/*#__PURE__*/ _jsx("div", {
        className: styles.settingsField,
        children: props.children
    });
export function EditNetTerms(props) {
    const { relationship } = props;
    const { t: amendmentsTexts } = useLocaleKeys(amendmentsTextsPath);
    const { t: editAgreementModalTexts } = useLocaleKeys('components.editAgreementModal');
    const { watch, control } = useEditAgreementForm();
    const updatedNetTerms = watch('updatedNetTerms');
    const netTermsChangeBullet = getNetTermsChangeBullet({
        relationship,
        updatedNetTerms,
        texts: amendmentsTexts
    });
    return /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: "updatedNetTerms",
        render: (p)=>{
            const _connectControllerToTextField = connectControllerToTextField(p), { onChange } = _connectControllerToTextField, rest = _object_without_properties(_connectControllerToTextField, [
                "onChange"
            ]);
            return /*#__PURE__*/ _jsxs(SettingsField, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        children: editAgreementModalTexts.setNetTermsDays()
                    }),
                    /*#__PURE__*/ _jsx(Select, _object_spread_props(_object_spread({
                        bi: {
                            name: 'net_terms_days'
                        },
                        collapseHelperText: false,
                        menuMaxHeight: 200,
                        width: 280,
                        testId: "edit-net-terms",
                        onChange: (e)=>onChange(+e.target.value)
                    }, rest), {
                        children: netTermsSelectOptions(amendmentsTexts).map((param)=>{
                            let { value, text } = param;
                            return /*#__PURE__*/ _jsx(MenuItem, {
                                value: value.toString(),
                                testId: "net-terms-opt-".concat(value),
                                children: text
                            }, value);
                        })
                    })),
                    netTermsChangeBullet && /*#__PURE__*/ _jsx(AmendmentChanges, {
                        disableNote: true,
                        disableBorderRadius: true,
                        items: [
                            netTermsChangeBullet
                        ],
                        isUnsaved: p.fieldState.isDirty,
                        testId: "net-terms-changes",
                        autoApprovalDate: relationshipModel.updatedNetTermsAutoApprovalDate(relationship),
                        onDiscard: ()=>onChange(relationship.netTerms)
                    })
                ]
            });
        }
    });
}
export function EditCCFees(props) {
    const { relationship } = props;
    const { t: amendmentsTexts } = useLocaleKeys(amendmentsTextsPath);
    const { control, watch } = useEditAgreementForm();
    const coverCreditCardFees = watch('coverCreditCardFees');
    const changeBullet = getCoverCCFeesChangeBullet({
        relationship,
        coverCreditCardFees,
        texts: amendmentsTexts
    });
    return /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: "coverCreditCardFees",
        render: (param)=>{
            let { field: { value, onChange }, fieldState: { isDirty } } = param;
            return /*#__PURE__*/ _jsxs(SettingsField, {
                children: [
                    /*#__PURE__*/ _jsx(CoverCreditCardFeesSelector, {
                        testId: "cc-fees",
                        vendorBusinessName: relationshipModel.vendorName(relationship),
                        clientBusinessName: relationshipModel.customerName(relationship),
                        value: value,
                        onChange: onChange
                    }),
                    changeBullet && /*#__PURE__*/ _jsx(AmendmentChanges, {
                        disableNote: true,
                        disableBorderRadius: true,
                        items: [
                            changeBullet
                        ],
                        isUnsaved: isDirty,
                        testId: "cc-fees-changes",
                        autoApprovalDate: relationshipModel.updatedIsVendorCoveringCCFeeAutoApprovalDate(relationship),
                        onDiscard: ()=>onChange(relationshipModel.isVendorCoveringCCFee(relationship))
                    })
                ]
            });
        }
    });
}
const EditApprovalMethodNoticePeriod = (param)=>{
    let { info, text, testId, fieldName, relationship, autoApprovalDate, bi, getApprovalMethodChangeBullet } = param;
    const { t: amendmentsTexts } = useLocaleKeys(amendmentsTextsPath);
    const { control, watch } = useEditAgreementForm();
    const approvalMethod = watch(fieldName);
    const noticePeriodDays = approvalMethodNoticePeriodModel.noticePeriodDays(approvalMethod);
    const changeBullet = getApprovalMethodChangeBullet({
        originalApprovalMethod: relationship[fieldName],
        newApprovalMethod: approvalMethod,
        texts: amendmentsTexts
    });
    return /*#__PURE__*/ _jsx(Controller, {
        name: fieldName,
        control: control,
        rules: {
            validate: (value)=>validateApprovalMethodNoticePeriod(approvalMethodNoticePeriodModel.isAutoApproval(value), approvalMethodNoticePeriodModel.noticePeriodDays(value) || '')
        },
        render: (param)=>{
            let { field: { value, onChange }, fieldState: { isDirty } } = param;
            return /*#__PURE__*/ _jsxs(SettingsField, {
                children: [
                    /*#__PURE__*/ _jsx(ApprovalMethodNoticePeriodField, {
                        testId: testId,
                        info: info,
                        text: text,
                        bi: bi,
                        enabled: approvalMethodNoticePeriodModel.isAutoApproval(value),
                        noticePeriod: noticePeriodDays != null ? noticePeriodDays : '',
                        onToggle: (enabled)=>{
                            if (enabled) {
                                const { maxDays } = getAutoApprovalRange();
                                onChange(approvalMethodNoticePeriodModel.makeAutoApprovalMethodNoticePeriod(maxDays));
                            } else {
                                onChange(approvalMethodNoticePeriodModel.makeManualApprovalMethodNoticePeriod());
                            }
                        },
                        onChangePeriod: (noticePeriod)=>{
                            if (noticePeriod === '') return;
                            onChange(approvalMethodNoticePeriodModel.makeAutoApprovalMethodNoticePeriod(noticePeriod));
                        }
                    }),
                    changeBullet && /*#__PURE__*/ _jsx(AmendmentChanges, {
                        disableNote: true,
                        disableBorderRadius: true,
                        items: [
                            changeBullet
                        ],
                        isUnsaved: isDirty,
                        testId: "".concat(testId, "-changes"),
                        autoApprovalDate: autoApprovalDate,
                        onDiscard: ()=>onChange(relationship[fieldName])
                    })
                ]
            });
        }
    });
};
export const EditAmendmentsApprovalMethod = (param)=>{
    let { relationship } = param;
    const { t: editAgreementModalTexts } = useLocaleKeys('components.editAgreementModal');
    return /*#__PURE__*/ _jsx(EditApprovalMethodNoticePeriod, {
        info: editAgreementModalTexts.amendmentsAutoApproveInfo(),
        text: editAgreementModalTexts.autoApproveAmendments(),
        testId: 'amendments-approval-method',
        fieldName: 'amendmentsApprovalMethod',
        bi: {
            enabledCheckbox: {
                name: 'amendments_auto_approval_enabled'
            },
            periodSelect: {
                name: 'amendments_auto_approval_period'
            }
        },
        relationship: relationship,
        autoApprovalDate: relationshipModel.updatedAmendmentsApprovalMethodAutoApprovalDate(relationship),
        getApprovalMethodChangeBullet: getAmendmentsApprovalMethodChangeBullet
    });
};
export const EditInvoicesApprovalMethod = (param)=>{
    let { relationship } = param;
    const { t: editAgreementModalTexts } = useLocaleKeys('components.editAgreementModal');
    return /*#__PURE__*/ _jsx(EditApprovalMethodNoticePeriod, {
        info: editAgreementModalTexts.invoicesAutoApproveInfo(),
        text: editAgreementModalTexts.autoApproveOutOfScope(),
        testId: 'invoices-approval-method',
        fieldName: 'invoicesApprovalMethod',
        bi: {
            enabledCheckbox: {
                name: 'invoices_auto_approval_enabled'
            },
            periodSelect: {
                name: 'invoices_auto_approval_period'
            }
        },
        relationship: relationship,
        autoApprovalDate: relationshipModel.updatedInvoicesApprovalMethodAutoApprovalDate(relationship),
        getApprovalMethodChangeBullet: getInvoicesApprovalMethodChangeBullet
    });
};
export function EditAllowManualPayments(props) {
    const { relationship } = props;
    const { t: amendmentsTexts } = useLocaleKeys(amendmentsTextsPath);
    const { control, watch } = useEditAgreementForm();
    const allowManualPayments = watch('allowManualPayments');
    const changeBullet = getAllowManualPaymentsChangeBullet({
        relationship,
        allowManualPayments,
        texts: amendmentsTexts
    });
    return /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: "allowManualPayments",
        render: (param)=>{
            let { field: { value, onChange }, fieldState: { isDirty } } = param;
            return /*#__PURE__*/ _jsxs(SettingsField, {
                children: [
                    /*#__PURE__*/ _jsx(AllowManualPaymentsCheckbox, {
                        value: value,
                        onChange: (newValue)=>onChange(newValue)
                    }),
                    changeBullet && /*#__PURE__*/ _jsx(AmendmentChanges, {
                        disableNote: true,
                        disableBorderRadius: true,
                        items: [
                            changeBullet
                        ],
                        isUnsaved: isDirty,
                        testId: "allow-manual-payments-changes",
                        autoApprovalDate: relationshipModel.updatedAllowManualPaymentsAutoApprovalDate(relationship),
                        onDiscard: ()=>onChange(relationship.allowManualPayments)
                    })
                ]
            });
        }
    });
}
